import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Paper,
  Grid,
  styled,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoCamera,
  CloudUpload,
  Visibility,
  Close as CloseIcon,
} from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { listClubs, setSelectedClub } from "../actions/clubActions";
import CustomSelect from "../components/Select";
import Loading from "../components/Loading";
import { useDialog } from '../components/DialogProvider';
import ScreenHeader from '../components/layout/ScreenHeader'
import { Building2 } from 'lucide-react';
import { FilterCard } from '../ui';

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const chipColors = {
  25000: { bg: "#3e2723", color: "#eceff1", label: "25K" },
  10000: { bg: "#bf360c", color: "#fff", label: "10K" },
  5000: { bg: "#1a237e", color: "#fff", label: "5K" },
  1000: { bg: "#004d40", color: "#fff", label: "1K" },
  500: { bg: "#b71c1c", color: "#fff", label: "500" },
  100: { bg: "#212121", color: "#fff", label: "100" },
};

const getTipTypeFromPath = (pathname) => {
  const normalizedPath = (pathname || "").toLowerCase().replace(/\/+$/, "");
  if (normalizedPath.endsWith("/tips/dealer")) return "dealer";
  if (normalizedPath.endsWith("/tips/service")) return "service";
  return "dealer";
};

const normalizeClubs = (clubItems = []) => {
  const normalized = [];
  const seen = new Set();

  clubItems.forEach((club) => {
    if (!club) return;

    const rawClubId = typeof club === "string" ? club : club._id || club.id;
    if (!rawClubId) return;

    const clubId = String(rawClubId);
    if (seen.has(clubId)) return;
    seen.add(clubId);

    normalized.push({
      _id: clubId,
      name:
        (typeof club === "object" && (club.name || club.clubCode)) ||
        "Assigned Club",
      clubCode: typeof club === "object" ? club.clubCode || "" : "",
    });
  });

  return normalized;
};

const getCurrentTipDateTimeValue = () => moment().format("YYYY-MM-DDTHH:mm:ss");

const formatTipTimestamp = (value) => {
  if (!value) return "N/A";

  const parsedValue = moment(value);
  if (!parsedValue.isValid()) return "N/A";

  return parsedValue.format("DD/MM/YYYY HH:mm:ss");
};

const TipsManagement = () => {
  const { confirm } = useDialog();
  const dispatch = useDispatch();
  const location = useLocation();
  const activeTipType = getTipTypeFromPath(location.pathname);
  const isDealerTips = activeTipType === "dealer";

  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    clubId: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1,
  });

  // Form State
  const [formData, setFormData] = useState({
    clubId: "",
    date: getCurrentTipDateTimeValue(),
    amount: "",
    notes: "",
  });

  // Chip Distribution State
  const [chipDistribution, setChipDistribution] = useState({});
  const chipDenominations = [25000, 10000, 5000, 1000, 500, 100];

  // Evidence State
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidencePreview, setEvidencePreview] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Evidence Timestamp & Location State
  const [evidenceTimestamp, setEvidenceTimestamp] = useState(null);
  const [evidenceLocation, setEvidenceLocation] = useState(null);

  // Evidence Modal State (for viewing existing tips evidence)
  const [viewEvidenceData, setViewEvidenceData] = useState(null);
  const [viewChipData, setViewChipData] = useState(null);

  const clubList = useSelector((state) => state.clubList);
  const { clubs = [] } = clubList;
  const adminLogin = useSelector((state) => state.adminLogin);
  const { userInfo } = adminLogin;

  // Global selected club from Redux
  const selectedClubState = useSelector((state) => state.selectedClub);
  const { clubId: globalSelectedClubId } = selectedClubState;
  const isSuperAdmin = userInfo?.role?.isSuperAdmin === true;
  const canReadClubsApi = isSuperAdmin || userInfo?.role?.clubsView === true;
  const assignedClubs = userInfo?.assignedClubs || [];
  const sessionAssignedClubs = useMemo(
    () => normalizeClubs(assignedClubs),
    [assignedClubs],
  );
  const apiClubs = useMemo(() => normalizeClubs(clubs), [clubs]);
  const accessibleClubs = useMemo(() => {
    if (canReadClubsApi && apiClubs.length > 0) {
      return apiClubs;
    }
    return sessionAssignedClubs;
  }, [canReadClubsApi, apiClubs, sessionAssignedClubs]);
  const singleAccessibleClubId =
    accessibleClubs.length === 1 ? accessibleClubs[0]._id : "";
  const hasNoAccessibleClubs = accessibleClubs.length === 0;
  const shouldShowClubFilter = accessibleClubs.length > 1;

  useEffect(() => {
    if (canReadClubsApi) {
      dispatch(listClubs());
    }
  }, [dispatch, canReadClubsApi]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeTipType]);

  // Sync global selectedClub to local filter
  useEffect(() => {
    if (globalSelectedClubId) {
      setFilters((prev) =>
        prev.clubId === globalSelectedClubId
          ? prev
          : { ...prev, clubId: globalSelectedClubId },
      );
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
      return;
    }

    if (singleAccessibleClubId) {
      setFilters((prev) =>
        prev.clubId === singleAccessibleClubId
          ? prev
          : { ...prev, clubId: singleAccessibleClubId },
      );
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
      return;
    }

    if (hasNoAccessibleClubs) {
      setFilters((prev) => (prev.clubId ? { ...prev, clubId: "" } : prev));
      setPagination((prev) => (prev.page === 1 ? prev : { ...prev, page: 1 }));
    }
  }, [globalSelectedClubId, singleAccessibleClubId, hasNoAccessibleClubs]);

  const fetchTips = async () => {
    if (!userInfo?.token) return;
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:9000";
      const params = new URLSearchParams();
      params.append("type", activeTipType);
      if (filters.clubId) params.append("clubId", filters.clubId);
      params.append("page", String(pagination.page));
      params.append("limit", String(pagination.limit));

      const { data } = await axios.get(`${baseUrl}/tips?${params.toString()}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      setTips(data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 1,
      }));
    } catch (error) {
      toast.error("Failed to fetch tips");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTips();
  }, [
    location.pathname,
    userInfo?.token,
    filters.clubId,
    activeTipType,
    pagination.page,
    pagination.limit,
  ]);

  // --- Chip Distribution Logics ---

  const handleAutoCalculate = () => {
    if (!formData.amount) return;
    const distribution = {};
    let remainingAmount = parseFloat(formData.amount) || 0;

    for (const denomination of chipDenominations) {
      if (remainingAmount >= denomination) {
        const chipCount = Math.floor(remainingAmount / denomination);
        distribution[denomination] = chipCount;
        remainingAmount = remainingAmount % denomination;
      }
    }
    setChipDistribution(distribution);
  };

  const handleChipChange = (denomination, value) => {
    const val = parseInt(value);
    const newDist = { ...chipDistribution, [denomination]: val };
    if (!value || val === 0) {
      delete newDist[denomination];
    }
    setChipDistribution(newDist);
  };

  const calculateTotalChips = () => {
    return Object.entries(chipDistribution).reduce((acc, [denom, count]) => {
      return acc + parseInt(denom) * (parseInt(count) || 0);
    }, 0);
  };

  const calculateChipDistributionTotal = (distribution = {}) => {
    return Object.entries(distribution).reduce((acc, [denom, count]) => {
      const denomination = parseInt(denom, 10);
      const quantity = parseInt(count, 10);
      if (!Number.isFinite(denomination) || !Number.isFinite(quantity)) {
        return acc;
      }
      return acc + denomination * quantity;
    }, 0);
  };

  const getSortedChipEntries = (distribution = {}) => {
    return Object.entries(distribution)
      .map(([denom, count]) => [parseInt(denom, 10), parseInt(count, 10)])
      .filter(
        ([denomination, quantity]) =>
          Number.isFinite(denomination) &&
          Number.isFinite(quantity) &&
          quantity > 0,
      )
      .sort((a, b) => b[0] - a[0]);
  };

  // --- Camera / Evidence Logics ---

  const captureLocationAndTimestamp = () => {
    setEvidenceTimestamp(new Date().toISOString());
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setEvidenceLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          console.warn("Geolocation error:", error.message);
          setEvidenceLocation(null);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be < 5MB");
        return;
      }
      setEvidenceFile(file);
      setEvidencePreview(URL.createObjectURL(file));
      setShowCamera(false);
      captureLocationAndTimestamp();
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      toast.error("Camera not supported or not HTTPS");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      setCameraStream(stream);
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      toast.error("Unable to access camera");
      console.error(err);
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            const file = new File([blob], `capture_${Date.now()}.jpg`, {
              type: "image/jpeg",
            });
            setEvidenceFile(file);
            setEvidencePreview(URL.createObjectURL(blob));
            captureLocationAndTimestamp();
            stopCamera();
          }
        },
        "image/jpeg",
        0.85,
      );
    }
  };

  // --- CRUD Operations ---

  const handleCreateTip = async () => {
    const resolvedClubId = formData.clubId || singleAccessibleClubId;
    if (!formData.date || !resolvedClubId || !formData.amount) {
      toast.error(
        hasNoAccessibleClubs
          ? "No club is assigned to your account. Please contact admin."
          : "Date, Club and Amount are required",
      );
      return;
    }

    const chipTotal = calculateTotalChips();
    if (chipTotal !== parseFloat(formData.amount)) {
      toast.error(
        `Chip total (₹${chipTotal}) must equal Amount (₹${formData.amount})`,
      );
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:9000";

      const submitData = new FormData();
      submitData.append("clubId", resolvedClubId);
      submitData.append("date", formData.date);
      submitData.append("amount", formData.amount);
      submitData.append("type", activeTipType);
      if (formData.notes) submitData.append("notes", formData.notes);

      if (evidenceFile) {
        submitData.append("evidence", evidenceFile);
        if (evidenceTimestamp) {
          submitData.append("evidenceTimestamp", evidenceTimestamp);
        }
        if (evidenceLocation) {
          submitData.append("evidenceLatitude", evidenceLocation.latitude);
          submitData.append("evidenceLongitude", evidenceLocation.longitude);
          if (evidenceLocation.accuracy) {
            submitData.append("evidenceAccuracy", evidenceLocation.accuracy);
          }
        }
      }

      submitData.append("chipDistribution", JSON.stringify(chipDistribution));

      await axios.post(`${baseUrl}/tips`, submitData, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("Tip added successfully");
      handleCloseDialog();
      fetchTips();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to add tip");
    }
  };

  const handleDeleteTip = async (id) => {
    if (!(await confirm({ title: "Delete tip", message: "Are you sure you want to delete this tip?", confirmText: "Delete", danger: true }))) return;
    try {
      const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:9000";
      await axios.delete(`${baseUrl}/tips/${id}`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      });
      toast.success("Tip deleted successfully");
      fetchTips();
    } catch (error) {
      toast.error("Failed to delete tip");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      clubId: filters.clubId || singleAccessibleClubId || "",
      date: getCurrentTipDateTimeValue(),
      amount: "",
      notes: "",
    });
    setChipDistribution({});
    setEvidenceFile(null);
    setEvidencePreview(null);
    setEvidenceTimestamp(null);
    setEvidenceLocation(null);
    stopCamera();
  };

  const handleOpenDialog = () => {
    if (hasNoAccessibleClubs) {
      toast.error("No club is assigned to your account. Please contact admin.");
      return;
    }

    const defaultClubId =
      filters.clubId || singleAccessibleClubId || accessibleClubs[0]?._id || "";

    setFormData((prev) => ({
      ...prev,
      clubId: defaultClubId,
      date: getCurrentTipDateTimeValue(),
    }));
    setOpenDialog(true);
  };

  const getEvidenceUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    const baseUrl = (
      process.env.REACT_APP_API_URL || "http://localhost:9000"
    ).replace(/\/api$/, "");
    return `${baseUrl}/${path}`;
  };

  const chipViewEntries = getSortedChipEntries(viewChipData?.distribution || {});
  const chipViewTotal = calculateChipDistributionTotal(
    viewChipData?.distribution || {},
  );

  const addTipButton = (
    <button
      className={`bg-green-500 hover:bg-green-700 text-white text-sm font-semibold py-2 px-4 rounded focus:outline-none focus:shadow-outline flex items-center gap-1 ${
        hasNoAccessibleClubs ? "opacity-50 cursor-not-allowed" : ""
      }`}
      onClick={handleOpenDialog}
      disabled={hasNoAccessibleClubs}
    >
      <AddIcon fontSize="small" /> Add Tip
    </button>
  );

  return (
    <div className="w-full h-full">
      <ScreenHeader title={isDealerTips ? "Dealer Tips" : "Service Tips"} />

      {/* Filter Bar */}
      {shouldShowClubFilter ? (
        <FilterCard
          id="tips-filters"
          className="my-3"
          inlineToggle
          open={filtersOpen}
          onOpenChange={setFiltersOpen}
          chips={filters.clubId
            ? [{ icon: Building2, label: accessibleClubs.find((club) => club._id === filters.clubId)?.name || 'Club' }]
            : []}
          emptyLabel="All clubs"
          aside={addTipButton}
        >
          <div className="flex flex-wrap gap-3 items-end">
            <CustomSelect
              width="w-52"
              name="Club"
              value={filters.clubId}
              onChange={(e) => {
                const value = e.target.value;
                dispatch(setSelectedClub(value || null));
                setFilters((prev) => ({ ...prev, clubId: value }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              options={accessibleClubs.map((club) => ({
                id: club._id,
                title: club.name,
              }))}
            />
          </div>
        </FilterCard>
      ) : (
        <div className="bg-white shadow-md rounded px-4 sm:px-8 py-3 sm:py-4 my-3">
          <div className="flex flex-wrap gap-3 items-end">{addTipButton}</div>
        </div>
      )}
      {hasNoAccessibleClubs && (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800">
          No club is assigned to your account. Please contact an administrator.
        </div>
      )}

      {/* Data Table */}
      {loading ? (
        <Loading />
      ) : (
        <div className="bg-white shadow-md rounded overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Club</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chips</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Evidence</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tips.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                    No tips found for the selected filters.
                  </td>
                </tr>
              ) : (
                tips.map((tip, idx) => (
                  <tr key={tip._id} className="hover:bg-gray-50">
                    {/* Paging is server-side, so continue numbering across pages */}
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {formatTipTimestamp(tip.date)}
                    </td>
                    <td className="px-4 py-3 text-sm">{tip.clubId?.name}</td>
                    <td className="px-4 py-3 text-sm font-medium">₹{tip.amount}</td>
                    <td className="px-4 py-3 text-sm">
                      {Object.keys(tip.chipDistribution || {}).length > 0 ? (
                        <button
                          type="button"
                          className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                          onClick={() =>
                            setViewChipData({
                              distribution: tip.chipDistribution || {},
                              amount: Number(tip.amount) || 0,
                            })
                          }
                        >
                          View Chips
                        </button>
                      ) : (
                        <span className="text-gray-400 italic">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {tip.evidence ? (
                        <div className="flex flex-col items-start gap-1">
                          <button
                            className="text-blue-600 hover:text-blue-800"
                            onClick={() =>
                              setViewEvidenceData({
                                url: getEvidenceUrl(tip.evidence),
                                timestamp: tip.evidenceTimestamp,
                                location: tip.evidenceLocation,
                              })
                            }
                          >
                            <Visibility fontSize="small" />
                          </button>
                          {tip.evidenceTimestamp && (
                            <span className="text-xs text-gray-500">
                              {new Date(tip.evidenceTimestamp).toLocaleString()}
                            </span>
                          )}
                          {tip.evidenceLocation?.latitude && (
                            <span className="text-xs text-gray-500">
                              {tip.evidenceLocation.address || `${tip.evidenceLocation.latitude.toFixed(4)}, ${tip.evidenceLocation.longitude.toFixed(4)}`}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">{tip.notes}</td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteTip(tip._id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 px-2">
        <p className="text-sm text-gray-500">
          Page {pagination.page} of {pagination.pages} ({pagination.total} total)
        </p>
        <div className="flex gap-2">
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={pagination.page <= 1}
            onClick={() =>
              setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))
            }
          >
            PREVIOUS
          </button>
          <button
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={pagination.page >= pagination.pages}
            onClick={() =>
              setPagination((prev) => ({
                ...prev,
                page: Math.min(prev.pages, prev.page + 1),
              }))
            }
          >
            NEXT
          </button>
        </div>
      </div>

      {/* ADD DIALOG (MUI Dialog kept for complex form) */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add {isDealerTips ? "Dealer" : "Service"} Tip</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* Row 1: Date & Club */}
            <Grid item xs={6}>
              <TextField
                label="Date & Time *"
                type="datetime-local"
                required
                fullWidth
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                inputProps={{ step: 1 }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              {singleAccessibleClubId ? (
                <TextField
                  label="Club"
                  fullWidth
                  value={accessibleClubs[0]?.name || "Assigned Club"}
                  InputProps={{ readOnly: true }}
                />
              ) : hasNoAccessibleClubs ? (
                <TextField
                  label="Club"
                  fullWidth
                  value="No assigned clubs"
                  InputProps={{ readOnly: true }}
                />
              ) : (
                <FormControl fullWidth>
                  <InputLabel>Club</InputLabel>
                  <MuiSelect
                    value={formData.clubId}
                    label="Club"
                    onChange={(e) =>
                      setFormData({ ...formData, clubId: e.target.value })
                    }
                  >
                    {accessibleClubs.map((club) => (
                      <MenuItem key={club._id} value={club._id}>
                        {club.name}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
              )}
            </Grid>

            {/* Row 2: Amount (Full Width) */}
            <Grid item xs={12}>
              <TextField
                label="Total Amount *"
                type="number"
                fullWidth
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Row 3: Chip Distribution Header */}
            <Grid
              item
              xs={12}
              sx={{
                mt: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1" fontWeight="bold">
                Chip Distribution (Mandatory)
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <MuiButton
                  variant="contained"
                  size="small"
                  onClick={handleAutoCalculate}
                >
                  Auto Calculate
                </MuiButton>
                <Typography
                  variant="body2"
                  color={
                    calculateTotalChips() !== parseFloat(formData.amount || 0)
                      ? "error"
                      : "success.main"
                  }
                  fontWeight="bold"
                >
                  Total: ₹{calculateTotalChips()}
                </Typography>
              </Box>
            </Grid>

            {/* Row 4: Chip Inputs */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  p: 3,
                  bgcolor: "var(--cm-surface-2)",
                  borderRadius: 2,
                  border: "1px solid var(--cm-border)",
                }}
              >
                <Grid container spacing={3} justifyContent="center">
                  {chipDenominations.map((denom) => {
                    const style = chipColors[denom] || {
                      bg: "#757575",
                      color: "#fff",
                      label: denom,
                    };
                    return (
                      <Grid item xs={4} sm={2} key={denom}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "50%",
                              bgcolor: style.bg,
                              color: style.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "bold",
                              fontSize: "0.85rem",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                              border: "2px dashed rgba(255,255,255,0.3)",
                              mb: 0.5,
                            }}
                          >
                            {style.label}
                          </Box>

                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              color: "text.secondary",
                              fontSize: "0.7rem",
                            }}
                          >
                            ₹{denom.toLocaleString()}
                          </Typography>

                          <TextField
                            size="small"
                            type="number"
                            placeholder="0"
                            value={chipDistribution[denom] || ""}
                            onChange={(e) =>
                              handleChipChange(denom, e.target.value)
                            }
                            InputProps={{
                              sx: {
                                borderRadius: 2,
                                bgcolor: "white",
                                "& input": {
                                  textAlign: "center",
                                  fontWeight: "bold",
                                },
                              },
                            }}
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>

            {/* Row 5: Evidence */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Evidence
              </Typography>

              {showCamera ? (
                <Box
                  sx={{
                    textAlign: "center",
                    border: "1px solid var(--cm-border)",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    style={{
                      width: "100%",
                      maxWidth: "400px",
                      height: "auto",
                      marginBottom: 10,
                    }}
                  />
                  <canvas ref={canvasRef} style={{ display: "none" }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 2 }}
                  >
                    <MuiButton
                      variant="contained"
                      color="primary"
                      onClick={capturePhoto}
                    >
                      Capture
                    </MuiButton>
                    <MuiButton
                      variant="outlined"
                      color="error"
                      onClick={stopCamera}
                    >
                      Cancel
                    </MuiButton>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <MuiButton
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUpload />}
                  >
                    Upload File
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </MuiButton>
                  <Typography variant="body2">OR</Typography>
                  <MuiButton
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={startCamera}
                  >
                    Use Camera
                  </MuiButton>
                </Box>
              )}

              {evidencePreview && !showCamera && (
                <Box
                  sx={{ mt: 2, position: "relative", display: "inline-block" }}
                >
                  <img
                    src={evidencePreview}
                    alt="Preview"
                    style={{ maxHeight: "150px", borderRadius: 4 }}
                  />
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -10,
                      bgcolor: "white",
                      "&:hover": { bgcolor: "var(--cm-surface-2)" },
                    }}
                    onClick={() => {
                      setEvidenceFile(null);
                      setEvidencePreview(null);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Grid>

            {/* Row 6: Remarks */}
            <Grid item xs={12}>
              <TextField
                label="Remarks (Optional)"
                fullWidth
                size="small"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseDialog}>Cancel</MuiButton>
          <MuiButton
            onClick={handleCreateTip}
            variant="contained"
            disabled={
              calculateTotalChips() !== parseFloat(formData.amount || 0)
            }
          >
            Save
          </MuiButton>
        </DialogActions>
      </Dialog>

      {/* Chip Distribution View Dialog */}
      <Dialog
        open={!!viewChipData}
        onClose={() => setViewChipData(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Chip Distribution
          <IconButton size="small" onClick={() => setViewChipData(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {chipViewEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No chip distribution available.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <table className="min-w-full text-sm">
                <thead>
                  <tr>
                    <th className="text-left py-1">Denomination</th>
                    <th className="text-right py-1">Qty</th>
                    <th className="text-right py-1">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {chipViewEntries.map(([denomination, quantity]) => (
                    <tr key={denomination}>
                      <td className="py-1">₹{denomination}</td>
                      <td className="text-right py-1">{quantity}</td>
                      <td className="text-right py-1">
                        ₹{denomination * quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Box
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: "1px solid var(--cm-border)",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Total Chips Value
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  ₹{chipViewTotal}
                </Typography>
              </Box>

              {viewChipData?.amount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Entered amount: ₹{viewChipData.amount}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Evidence View Dialog */}
      <Dialog
        open={!!viewEvidenceData}
        onClose={() => setViewEvidenceData(null)}
        maxWidth="lg"
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          Evidence
          <IconButton size="small" onClick={() => setViewEvidenceData(null)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {viewEvidenceData && (
            <Box>
              <img
                src={viewEvidenceData.url}
                alt="Evidence"
                style={{ width: "100%", height: "auto", borderRadius: 4 }}
              />
              <Box sx={{ mt: 2, p: 2, bgcolor: "var(--cm-surface-2)", borderRadius: 1 }}>
                {viewEvidenceData.timestamp && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Captured At:</strong>{" "}
                    {new Date(viewEvidenceData.timestamp).toLocaleString()}
                  </Typography>
                )}
                {viewEvidenceData.location?.latitude && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Location:</strong>{" "}
                    {viewEvidenceData.location.address ||
                      `${viewEvidenceData.location.latitude.toFixed(6)}, ${viewEvidenceData.location.longitude.toFixed(6)}`}
                    {viewEvidenceData.location.accuracy && (
                      <span style={{ color: "var(--cm-text-muted)", marginLeft: 8 }}>
                        (accuracy: ~{Math.round(viewEvidenceData.location.accuracy)}m)
                      </span>
                    )}
                  </Typography>
                )}
                {viewEvidenceData.location?.latitude && (
                  <Typography variant="body2">
                    <strong>Coordinates:</strong>{" "}
                    {viewEvidenceData.location.latitude.toFixed(6)}, {viewEvidenceData.location.longitude.toFixed(6)}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TipsManagement;
